// Updated ServerPlugins with framer-motion layout animations and restored confirmation and scheduling dialogs
import React, { useEffect, useState } from 'react';
import {
    Box, Button, Container, Typography, Paper, Stack, Switch, Divider, Grid, TextField,
    IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
    deletePlugin,
    enablePlugin,
    getPlugins,
    indexServer,
    installPlugin,
    pinnedPlugin,
    updatePlugin
} from '../http/ServerAPI';
import {indexPlugins, updatePluginFile} from '../http/PluginAPI';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import PushPinIcon from '@mui/icons-material/PushPin';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { observer } from 'mobx-react-lite';
import {useNavigate, useParams} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Skeleton from '@mui/material/Skeleton';
import EditIcon from "@mui/icons-material/Edit";
import BreadCrumbs from "../components/Breadcrumbs";



const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
};

const ServerPlugins = observer(() => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('');
    const [pinnedPlugins, setPinnedPlugins] = useState([]);
    const [scheduledPlugins, setScheduledPlugins] = useState({});
    const [confirmAction, setConfirmAction] = useState(null);
    const [scheduleDialog, setScheduleDialog] = useState({ open: false, plugin: '', rules: [] });
    const [serverPlugins, setServerPlugins] = useState([]);
    const [indexPluginsList, setIndexPluginsList] = useState([]);
    const [infoIndexServer, setInfoIndexServer] = useState([]);
    const [loadingServerPlugins, setLoadingServerPlugins] = useState(true);
    const [loadingIndexPlugins, setLoadingIndexPlugins] = useState(true);
    const [loadingIndexServer, setLoadingIndexServer] = useState(true);

    const hrefs = [
        { href: `/servers`, name: 'Список серверов' }
    ];

    useEffect(() => {
        setLoadingServerPlugins(true);
        setLoadingIndexPlugins(true);
        setLoadingIndexServer(true);

        getPlugins(id)
            .then(setServerPlugins)
            .finally(() => setLoadingServerPlugins(false));

        indexPlugins()
            .then(setIndexPluginsList)
            .finally(() => setLoadingIndexPlugins(false));

        indexServer(id)
            .then(setInfoIndexServer)
            .finally(() => setLoadingIndexServer(false));
    }, [id]);

    const confirm = (action, plugin, message) => {
        setConfirmAction({ action, plugin, message });
    };

    const executeConfirmAction = () => {
        if (confirmAction?.action && confirmAction?.plugin) {
            confirmAction.action(confirmAction.plugin);
        }
        setConfirmAction(null);
    };

    const handleEnable = async (plugin, status) => {
        try {
            await enablePlugin(id, plugin.plugin_id, status);
            getPlugins(id).then(setServerPlugins);
        } catch (e) {
            console.error(e);
        }
    };

    const handlePinned = async (plugin, status) => {
        try {
            await pinnedPlugin(id, plugin.plugin_id, status);
        } catch (e) {
            console.error(e);
        }
    };

    const handleInstall = (plugin) => {
        confirm(async () => {
            console.log('Installing', plugin.plugin_name);
            try {
                await installPlugin(id, plugin.plugin_id);
                getPlugins(id).then(setServerPlugins);
            } catch (e) {
                console.error(e);
            }
        }, plugin, `Установить плагин ${plugin.plugin_name}?`);
    };

    const handleRemove = (plugin) => {
        confirm(async () => {
            console.log('Removing', plugin.name);
            try {
                await deletePlugin(id, plugin.plugin_id);
                getPlugins(id).then(setServerPlugins);
            } catch (e) {
                console.error(e);
            }
        }, plugin, `Удалить плагин ${plugin.name}?`);
    };

    const handleUpdate = (plugin) => {
        confirm(async() => {
            console.log('Updating', plugin.name);
            try {
                await updatePlugin(id, plugin.plugin_id);
                getPlugins(id).then(setServerPlugins);
            } catch (e) {
                console.error(e);
            }
        }, plugin, `Обновить плагин ${plugin.name}?`);
    };

    const handleScheduleToggle = (name) => {
        const existing = scheduledPlugins[name] || [];
        setScheduleDialog({ open: true, plugin: name, rules: [...existing] });
    };

    const applySchedule = () => {
        setScheduledPlugins(prev => ({
            ...prev,
            [scheduleDialog.plugin]: [...scheduleDialog.rules]
        }));
        setScheduleDialog({ open: false, plugin: '', rules: [] });
    };

    const addScheduleRule = () => {
        setScheduleDialog(prev => ({
            ...prev,
            rules: [...prev.rules, { type: 'custom', custom: '', start: '', end: '' }]
        }));
    };

    const updateRule = (index, field, value) => {
        const newRules = [...scheduleDialog.rules];
        newRules[index][field] = value;
        setScheduleDialog(prev => ({ ...prev, rules: newRules }));
    };

    const removeRule = (index) => {
        const newRules = [...scheduleDialog.rules];
        newRules.splice(index, 1);
        setScheduleDialog(prev => ({ ...prev, rules: newRules }));
    };

    const renderSkeletons = (count = 5) =>
        Array.from({ length: count }).map((_, i) => (
            <motion.div key={`skeleton-${i}`} layout variants={fadeIn} initial="hidden" animate="visible" exit="exit">
                <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
                </Paper>
            </motion.div>
        ));

    const renderPluginCard = (plugin, source = 'server') => {
        const match = source === 'server'
            ? indexPluginsList.find(p => p.plugin_name.toLowerCase() === plugin.name.toLowerCase())
            : null;
        const versionMismatch = source === 'server' && match && match.version !== plugin.version;
        const pluginName = source === 'server' ? plugin.name : plugin.plugin_name;
        const pluginVersion = plugin.version;
        const pluginDate = plugin.date;
        const rules = scheduledPlugins[pluginName] || [];

        return (
            <motion.div key={pluginName} layout variants={fadeIn} initial="hidden" animate="visible" exit="exit">
                <Paper sx={{ p: 2, mb: 2, border: plugin.error ? '1px solid #f44336' : 'none' }} elevation={2}>
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1">
                                {pluginName}
                            </Typography>

                            <Stack direction="row" spacing={1}>
                                {source === 'server' && (
                                    <>
                                        <Tooltip title="Закрепить">
                                            <IconButton onClick={() => {
                                                setPinnedPlugins(prev => prev.includes(pluginName) ? prev.filter(p => p !== pluginName) : [...prev, pluginName]);
                                                plugin.pinned = !plugin.pinned;
                                                handlePinned(plugin, plugin.pinned);
                                                setServerPlugins([...serverPlugins]);
                                            }}>
                                                <PushPinIcon color={plugin.pinned ? 'primary' : 'disabled'} fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Управление по расписанию">
                                            <IconButton onClick={() => handleScheduleToggle(pluginName)}>
                                                <ScheduleIcon color={rules.length > 0 ? 'primary' : 'disabled'} fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        {source === 'server' && (
                            <Typography variant="caption">
                                Конфиг плагина:
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                        navigate(`/server/plugins/${id}/edit/0/${plugin.plugin_id}`)
                                    }
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Typography>
                        )}
                        <Typography variant="caption">
                            Версия: {pluginVersion || '—'}
                            {versionMismatch && match && (
                                <> <WarningIcon fontSize="inherit" color="warning" /> → {match.version}</>
                            )}
                        </Typography>
                        <Typography variant="caption">
                            {pluginDate || ''}
                        </Typography>
                        {plugin.error && (
                            <Box sx={{ bgcolor: '#ffebee', p: 1, borderRadius: 1, mt: 1 }}>
                                <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                    Ошибка при запуске плагина
                                </Typography>
                                <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {plugin.error.length > 250
                                        ? `${plugin.error.slice(0, 250)}...`
                                        : plugin.error}
                                </Typography>
                            </Box>
                        )}
                        {rules.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Расписание: {rules.map(r => {
                                const parts = [];
                                if (r.type === 'custom' && r.custom) parts.push(`Польз: ${r.custom}`);
                                else if (r.type === 'beforeWipe') parts.push('−2д до вайпа');
                                else if (r.type === 'afterWipe') parts.push('+после вайпа');
                                if (r.start && r.end) parts.push(`(${r.start} → ${r.end})`);
                                return parts.join(' ');
                            }).join(', ')}
                            </Typography>
                        )}
                        {source === 'server' ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Switch checked={plugin.enabled} onChange={() => {
                                    plugin.enabled = !plugin.enabled;
                                    handleEnable(plugin, plugin.enabled);
                                    setServerPlugins([...serverPlugins]);
                                }} />
                                {versionMismatch && (
                                    <Button size="small" startIcon={<UpdateIcon />} onClick={() => handleUpdate(plugin)}>Обновить</Button>
                                )}
                                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleRemove(plugin)}>Удалить</Button>
                            </Stack>
                        ) : (
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleInstall(plugin)}>Установить</Button>
                        )}
                    </Stack>
                </Paper>
            </motion.div>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3, marginBottom: 3 }}>
                <BreadCrumbs hrefs={hrefs} name={`Управление плагинами сервера ${infoIndexServer.title || ''}`} />
            </Box>

            <Typography variant="h4" gutterBottom>Управление плагинами сервера {infoIndexServer.title || ''}</Typography>
            <Divider sx={{ mb: 3 }} />
            <TextField label="Фильтр по имени плагина" variant="outlined" fullWidth value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ mb: 4 }} />
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Плагины на сервере</Typography>
                    <AnimatePresence>
                        {loadingServerPlugins
                            ? renderSkeletons()
                            : serverPlugins
                                .filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
                                .sort((a, b) => {
                                    if (b.pinned === a.pinned) return a.name.localeCompare(b.name);
                                    return (b.pinned === true) - (a.pinned === true);
                                })
                                .map(p => renderPluginCard(p, 'server'))}
                    </AnimatePresence>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Доступна установка</Typography>
                    <AnimatePresence>
                        {loadingIndexPlugins
                            ? renderSkeletons()
                            : indexPluginsList
                                .filter(p => !serverPlugins.map(sp => sp.name.toLowerCase()).includes(p.plugin_name.toLowerCase()))
                                .filter(p => p.plugin_name.toLowerCase().includes(filter.toLowerCase()))
                                .sort((a, b) => pinnedPlugins.includes(b.plugin_name) - pinnedPlugins.includes(a.plugin_name))
                                .map(p => renderPluginCard(p, 'index'))}
                    </AnimatePresence>
                </Grid>
            </Grid>

            <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmAction?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction(null)}>Отмена</Button>
                    <Button onClick={executeConfirmAction} autoFocus>Подтвердить</Button>
                </DialogActions>
            </Dialog>

            <Dialog maxWidth="md" fullWidth open={scheduleDialog.open} onClose={() => setScheduleDialog({ open: false, plugin: '', rules: [] })}>
                <DialogTitle>Настройка расписания</DialogTitle>
                <DialogContent>
                    {scheduleDialog.rules.map((rule, index) => (
                        <Stack key={index} spacing={1} direction="column" sx={{ my: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Тип правила</InputLabel>
                                <Select value={rule.type} label="Тип правила" onChange={(e) => updateRule(index, 'type', e.target.value)}>
                                    <MenuItem value="beforeWipe">Отключить за 2 дня до вайпа</MenuItem>
                                    <MenuItem value="afterWipe">Включить после вайпа</MenuItem>
                                    <MenuItem value="custom">Пользовательское</MenuItem>
                                </Select>
                            </FormControl>
                            {rule.type === 'custom' && (
                                <>
                                    <TextField fullWidth label="Пользовательское условие" value={rule.custom || ''} onChange={(e) => updateRule(index, 'custom', e.target.value)} />
                                    <TextField type="datetime-local" label="Начало действия" value={rule.start || ''} onChange={(e) => updateRule(index, 'start', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                                    <TextField type="datetime-local" label="Окончание действия" value={rule.end || ''} onChange={(e) => updateRule(index, 'end', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                                </>
                            )}
                            <Button variant="outlined" color="error" onClick={() => removeRule(index)}>Удалить правило</Button>
                            <Divider />
                        </Stack>
                    ))}
                    <Button onClick={addScheduleRule}>+ Добавить правило</Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScheduleDialog({ open: false, plugin: '', rules: [] })}>Отмена</Button>
                    <Button onClick={applySchedule}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default ServerPlugins;